// Planet curvature post pass: a subtle full-screen barrel bulge that reads as
// a curved planet surface / CRT face, with horizon edge darkening, a faint
// atmosphere tint, and a slight chromatic shift toward the rim. Runs after
// OutputPass on display-referred pixels, before the CRT/VHS composite.
// One pass, three texture taps — cheap enough to leave on at 200 enemies.
// Follows the three.js ShaderPass shader-object convention
// ({ uniforms, vertexShader, fragmentShader }), same as crt-composite.ts.

import * as THREE from 'three';

export const PlanetCurvatureShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    // Barrel bulge amount; 0 disables the warp, 0.25 is the subtle default.
    strength: { value: 0.25 },
    // Normalized screen radius where the horizon effects reach full strength
    // (0.5 is the screen edge midpoint, ~0.707 the corner).
    radius: { value: 0.72 },
    // Atmosphere tint added at the horizon rim (display-referred).
    tint: { value: new THREE.Color(0x2e6f8e) }
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float strength;
    uniform float radius;
    uniform vec3 tint;
    varying vec2 vUv;

    void main() {
      vec2 cc = vUv - 0.5;
      float r2 = dot(cc, cc);

      // Subtle barrel (planet bulge) curvature; the 0.5 factor keeps the
      // 0..1 strength range gentle so gameplay stays readable.
      vec2 uv = vUv + cc * r2 * strength * 0.5;

      // Horizon factor: 0 near the center, 1 at/behind the radius. The gap
      // between edge midpoints (r2 = 0.25) and corners (r2 = 0.5) makes the
      // effect concentrate toward the corners, like a planet limb.
      float horizon = smoothstep(radius * radius * 0.45, radius * radius, r2);

      // Faint chromatic shift, only near the rim.
      vec2 ab = cc * horizon * strength * 0.015;
      vec3 col;
      col.r = texture2D(tDiffuse, uv + ab).r;
      col.g = texture2D(tDiffuse, uv).g;
      col.b = texture2D(tDiffuse, uv - ab).b;

      // Outside the original frame (only possible with the bulge) sample
      // black so the warp reads as a curved limb, not smeared edge pixels.
      float inFrame = step(0.0, uv.x) * step(uv.x, 1.0)
                    * step(0.0, uv.y) * step(uv.y, 1.0);
      col *= inFrame;

      // Horizon darkening + faint atmosphere tint.
      col *= 1.0 - horizon * 0.30;
      col += tint * horizon * 0.10;

      gl_FragColor = vec4(col, 1.0);
    }
  `
};
